// Admin Trust & Safety Service
// STRICTLY LOCKED DOWN TO coryliseno@gmail.com
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, getDoc, query, orderBy } from 'firebase/firestore';
import type { ConcernReport, BannedEmail } from '../types';
import { getCurrentProfile } from './authService';

export const ADMIN_EMAIL = 'coryliseno@gmail.com';

/**
 * Verify if current active session belongs to the designated administrator
 */
export function isAdmin(): boolean {
  const profile = getCurrentProfile();
  return Boolean(profile && profile.email && profile.email.toLowerCase().trim() === ADMIN_EMAIL);
}

/**
 * Guard function to prevent any non-admin execution
 */
export function assertAdmin(): void {
  if (!isAdmin()) {
    throw new Error('Access Denied: Action strictly restricted to coryliseno@gmail.com');
  }
}

/**
 * Subscribe to all live concern reports (Admin only)
 */
export function subscribeReports(callback: (reports: ConcernReport[]) => void): () => void {
  assertAdmin();

  const colRef = collection(db, 'reports');
  const q = query(colRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const list: ConcernReport[] = [];
    snapshot.forEach(d => {
      list.push({ id: d.id, ...d.data() } as ConcernReport);
    });
    callback(list);
  }, (err) => {
    console.warn('[Pops Culture Admin] Reports listener error:', err);
  });
}

/**
 * Subscribe to the banned emails registry
 */
export function subscribeBannedEmails(callback: (emails: BannedEmail[]) => void): () => void {
  const colRef = collection(db, 'bannedEmails');
  return onSnapshot(colRef, (snapshot) => {
    const list: BannedEmail[] = [];
    snapshot.forEach(d => {
      list.push(d.data() as BannedEmail);
    });
    callback(list);
  }, (err) => {
    console.warn('[Pops Culture Admin] Banned emails listener warning:', err);
  });
}

/**
 * Check if a given email is currently banned from Pops Culture
 */
export async function checkIsEmailBanned(email: string): Promise<boolean> {
  if (!email) return false;
  try {
    const sanitizedEmail = email.toLowerCase().trim();
    const docRef = doc(db, 'bannedEmails', sanitizedEmail);
    const snap = await getDoc(docRef);
    return snap.exists();
  } catch (err) {
    console.warn('[Pops Culture] Banned email check error:', err);
    return false;
  }
}

/**
 * Admin action: Relist an event when review finds NO rule violation
 */
export async function relistEvent(reportId: string, eventId: string, notes: string = ''): Promise<void> {
  assertAdmin();
  const now = Date.now();

  // Relist event
  const missionRef = doc(db, 'missions', eventId);
  await updateDoc(missionRef, {
    status: 'active',
    reportedReason: null,
    reportedBy: null,
    reportedAt: null
  });

  // Resolve report
  const reportRef = doc(db, 'reports', reportId);
  await updateDoc(reportRef, {
    status: 'resolved_cleared',
    resolvedAt: now,
    resolvedBy: ADMIN_EMAIL,
    adminNotes: notes || 'Event cleared and relisted by admin. No violation found.'
  });
}

/**
 * Admin action: Permanently remove an event for rule violation
 */
export async function removeEvent(reportId: string, eventId: string, notes: string = ''): Promise<void> {
  assertAdmin();
  const now = Date.now();

  const missionRef = doc(db, 'missions', eventId);
  await updateDoc(missionRef, {
    status: 'removed'
  });

  const reportRef = doc(db, 'reports', reportId);
  await updateDoc(reportRef, {
    status: 'resolved_violation',
    resolvedAt: now,
    resolvedBy: ADMIN_EMAIL,
    adminNotes: notes || 'Event removed for community safety / guidelines violation.'
  });
}

/**
 * Admin action: Remove event AND permanently ban the event's creator
 */
export async function removeEventAndBanCreator(
  reportId: string,
  eventId: string,
  creatorUid: string,
  creatorEmail: string,
  notes: string = ''
): Promise<void> {
  assertAdmin();
  const now = Date.now();

  // 1. Remove event
  const missionRef = doc(db, 'missions', eventId);
  await updateDoc(missionRef, { status: 'removed' });

  // 2. Ban creator user profile
  if (creatorUid) {
    const userRef = doc(db, 'users', creatorUid);
    await updateDoc(userRef, {
      status: 'banned',
      underReview: false,
      suspensionReason: 'Banned by admin for event violation'
    });
  }

  // 3. Register email in bannedEmails collection
  if (creatorEmail) {
    const cleanEmail = creatorEmail.toLowerCase().trim();
    const bannedRef = doc(db, 'bannedEmails', cleanEmail);
    await setDoc(bannedRef, {
      email: cleanEmail,
      bannedAt: now,
      bannedBy: ADMIN_EMAIL,
      reason: notes || 'Violated site rules via created event',
      reportId
    });
  }

  // 4. Mark report resolved
  const reportRef = doc(db, 'reports', reportId);
  await updateDoc(reportRef, {
    status: 'resolved_violation',
    resolvedAt: now,
    resolvedBy: ADMIN_EMAIL,
    adminNotes: notes || `Event removed and creator ${creatorEmail} banned from platform.`
  });
}

/**
 * Admin action: Re-enable user account when review finds NO rule violation
 */
export async function reenableUserAccount(reportId: string, targetUid: string, notes: string = ''): Promise<void> {
  assertAdmin();
  const now = Date.now();

  const userRef = doc(db, 'users', targetUid);
  await updateDoc(userRef, {
    status: 'active',
    underReview: false,
    suspensionReason: null,
    reportedBy: null
  });

  const reportRef = doc(db, 'reports', reportId);
  await updateDoc(reportRef, {
    status: 'resolved_cleared',
    resolvedAt: now,
    resolvedBy: ADMIN_EMAIL,
    adminNotes: notes || 'User account re-enabled. Review deemed no violation.'
  });
}

/**
 * Admin action: Permanently ban reported user and add email to ban list
 */
export async function banReportedUser(
  reportId: string,
  targetUid: string,
  targetEmail: string,
  notes: string = ''
): Promise<void> {
  assertAdmin();
  const now = Date.now();

  // 1. Ban user account
  const userRef = doc(db, 'users', targetUid);
  await updateDoc(userRef, {
    status: 'banned',
    underReview: false,
    suspensionReason: notes || 'Banned by admin for site rule violation'
  });

  // 2. Add email to banned list
  if (targetEmail) {
    const cleanEmail = targetEmail.toLowerCase().trim();
    const bannedRef = doc(db, 'bannedEmails', cleanEmail);
    await setDoc(bannedRef, {
      email: cleanEmail,
      bannedAt: now,
      bannedBy: ADMIN_EMAIL,
      reason: notes || 'Reported user banned after admin review',
      reportId
    });
  }

  // 3. Mark report resolved
  const reportRef = doc(db, 'reports', reportId);
  await updateDoc(reportRef, {
    status: 'resolved_violation',
    resolvedAt: now,
    resolvedBy: ADMIN_EMAIL,
    adminNotes: notes || `User ${targetEmail} banned permanently.`
  });
}

/**
 * Admin action: Ban the reporting user if they were flagging maliciously/incorrectly on purpose
 */
export async function banReportingUser(
  reportId: string,
  reporterUid: string,
  reporterEmail: string,
  notes: string = ''
): Promise<void> {
  assertAdmin();
  const now = Date.now();

  // 1. Ban reporting user account
  const userRef = doc(db, 'users', reporterUid);
  await updateDoc(userRef, {
    status: 'banned',
    underReview: false,
    suspensionReason: 'Banned for malicious and fraudulent flagging of content'
  });

  // 2. Add reporter email to banned registry
  if (reporterEmail) {
    const cleanEmail = reporterEmail.toLowerCase().trim();
    const bannedRef = doc(db, 'bannedEmails', cleanEmail);
    await setDoc(bannedRef, {
      email: cleanEmail,
      bannedAt: now,
      bannedBy: ADMIN_EMAIL,
      reason: notes || 'Malicious / bad-faith reporting of members or events',
      reportId
    });
  }

  // 3. Dismiss report with note
  const reportRef = doc(db, 'reports', reportId);
  await updateDoc(reportRef, {
    status: 'dismissed',
    resolvedAt: now,
    resolvedBy: ADMIN_EMAIL,
    adminNotes: notes || `Report dismissed. Reporting user ${reporterEmail} banned for malicious abuse.`
  });
}

/**
 * Admin action: Unban an email address
 */
export async function unbanEmail(email: string): Promise<void> {
  assertAdmin();
  const cleanEmail = email.toLowerCase().trim();
  const docRef = doc(db, 'bannedEmails', cleanEmail);
  await deleteDoc(docRef);
}
